import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { X, Minus, Plus, Trash2, ShoppingBag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { trackEvent } from "@/components/Analytics";
import { CheckoutDetailsDialog } from "@/components/CheckoutDetailsDialog";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n.toLocaleString("en-NZ");

// Slide-out cart drawer — lists every item queued via "Add to Cart" across
// the site (persisted to localStorage by CartContext), lets the customer
// adjust quantity/remove lines, then hands the whole cart to Stripe in ONE
// checkout session (POST /api/checkout/cart-session), after a quick
// on-site details step. Shipping (Delivery vs free Christchurch pickup) is
// resolved server-side once the cart is submitted — Stripe itself presents
// the final choice on its hosted page.
export const CartDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, subtotalCents, totalCount } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleCheckout = () => {
    trackEvent("cart_checkout_click");
    setCheckoutError(null);
    setShowDetails(true);
  };

  const handleDetailsSubmit = async (customer) => {
    setIsCheckingOut(true);
    try {
      const { data } = await axios.post(`${API}/api/checkout/cart-session`, {
        items: items.map((i) => ({
          product_path: i.productPath,
          quantity: i.quantity,
          selected_options: i.selectedOptions || null,
        })),
        origin_url: window.location.origin,
        customer,
      });
      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong starting checkout. Please try again.";
      setCheckoutError(detail);
      toast.error(detail);
      setIsCheckingOut(false);
    }
  };

  return (
    <>
      <div
        data-testid="cart-drawer-overlay"
        onClick={() => setIsOpen(false)}
        className={`fixed inset-0 z-[60] bg-black/70 transition-opacity duration-300 ${isOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[61] h-full w-full sm:max-w-md bg-[var(--bg-secondary)] border-l border-[var(--border-default)] flex flex-col transition-transform duration-500 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border-default)]">
          <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-white">
            Your Cart {totalCount > 0 && `(${totalCount})`}
          </h2>
          <button
            type="button"
            aria-label="Close cart"
            data-testid="cart-drawer-close"
            onClick={() => setIsOpen(false)}
            className="text-white/60 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="cart-drawer-empty">
            <ShoppingBag size={40} className="text-white/20" />
            <p className="text-sm text-[var(--text-secondary)]">Your cart is empty.</p>
            <button type="button" onClick={() => setIsOpen(false)} className="btn-secondary text-xs">
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-5" data-testid="cart-drawer-items">
              {items.map((item) => (
                <div key={item.id} data-testid={`cart-line-item-${item.id}`} className="flex gap-4">
                  <img src={item.thumb} alt={item.name} className="w-20 h-20 object-cover shrink-0 bg-black/20" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white leading-snug">{item.name}</p>
                    {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                      <p className="text-xs text-[var(--text-secondary)] mt-1">
                        {Object.values(item.selectedOptions).join(", ")}
                      </p>
                    )}
                    <p className="font-mono text-xs text-[var(--brand-primary)] mt-1">
                      {item.priceFrom ? "FROM " : ""}${fmt(item.unitPrice)}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-[var(--border-default)]">
                        <button
                          type="button"
                          aria-label="Decrease quantity"
                          data-testid={`cart-qty-decrease-${item.id}`}
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          className="px-2 py-1 text-white/70 hover:text-white disabled:opacity-30 transition-colors"
                        >
                          <Minus size={12} />
                        </button>
                        <span data-testid={`cart-qty-value-${item.id}`} className="px-3 text-sm font-mono text-white">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          aria-label="Increase quantity"
                          data-testid={`cart-qty-increase-${item.id}`}
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          disabled={item.quantity >= 10}
                          className="px-2 py-1 text-white/70 hover:text-white disabled:opacity-30 transition-colors"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <button
                        type="button"
                        aria-label="Remove item"
                        data-testid={`cart-remove-${item.id}`}
                        onClick={() => removeItem(item.id)}
                        className="text-white/40 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[var(--border-default)] px-6 py-5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-white/70">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-display text-2xl text-white">${fmt(subtotalCents / 100)}</span>
              </div>
              <p className="text-xs text-[var(--text-secondary)] mb-5">GST included · Shipping calculated at checkout</p>
              <button
                type="button"
                data-testid="cart-checkout-button"
                onClick={handleCheckout}
                disabled={isCheckingOut}
                className="btn-primary w-full justify-center disabled:opacity-70"
              >
                {isCheckingOut ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : "Checkout"}
              </button>
              <Link to="/" onClick={() => setIsOpen(false)} className="block text-center text-xs text-white/50 hover:text-white mt-4 transition-colors">
                Continue Shopping
              </Link>
            </div>
          </>
        )}
      </aside>
      <CheckoutDetailsDialog
        open={showDetails}
        onOpenChange={setShowDetails}
        onSubmit={handleDetailsSubmit}
        submitting={isCheckingOut}
        error={checkoutError}
      />
    </>
  );
};
